import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { cn } from '../utils/cn'
import { Loader2 } from 'lucide-react'

function useDebouncedCallback<TArgs extends unknown[], TResult>(
  callback: (...args: TArgs) => TResult,
  delay: number
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const run = useCallback(
    (...args: TArgs) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        callback(...args)
      }, delay)
    },
    [callback, delay]
  )

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return useMemo(() => ({ run, flush }), [run, flush])
}

export interface EditablePropertyProps {
  value: string | null | undefined
  recordId: string
  field: string
  multiline?: boolean
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
  onSave: (recordId: string, field: string, value: string) => Promise<void>
  onDraftChange?: (value: string) => void
  autoSave?: boolean
  className?: string
}

export function EditableProperty({
  value,
  recordId,
  field,
  multiline = false,
  inputRef,
  onSave,
  onDraftChange,
  autoSave = true,
  className,
}: EditablePropertyProps) {
  const [local, setLocal] = useState(value ?? '')
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle')
  const prev = useRef(value ?? '')
  const latestVal = useRef(local)

  useEffect(() => {
    latestVal.current = local
  }, [local])

  useEffect(() => {
    const next = value ?? ''
    setLocal(next)
    prev.current = next
    setStatus('idle')
  }, [recordId, field, value])

  const doSave = async (val: string) => {
    if (val === prev.current) return
    setStatus('saving')
    try {
      await onSave(recordId, field, val)
      prev.current = val
      setStatus('idle')
    } catch {
      setLocal(prev.current)
      setStatus('error')
    }
  }

  const saveDebounced = useDebouncedCallback(doSave, 500)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextValue = e.target.value
    setLocal(nextValue)
    if (!autoSave) {
      onDraftChange?.(nextValue)
      return
    }
    saveDebounced.run(nextValue)
  }

  const handleBlur = () => {
    if (!autoSave) return
    saveDebounced.flush()
    void doSave(latestVal.current)
  }

  const Tag = multiline ? 'textarea' : 'input'

  return (
    <div className={cn('relative w-full group/input', multiline ? 'min-h-[72px]' : 'min-h-0', className)}>
      <Tag
        ref={inputRef as never}
        value={local}
        placeholder="ยังไม่ระบุ"
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(
          'w-full border-0 bg-transparent resize-none p-0 text-sm font-semibold leading-6 text-aera-gray shadow-none placeholder:text-aera-lightslate focus:outline-none focus:ring-0 transition-colors duration-200',
          'hover:text-aera-charcoal cursor-text',
          'focus:text-aera-charcoal',
          autoSave && status === 'saving' && 'text-aera-lightslate pr-8',
          autoSave && status === 'error' && 'text-(--aera-dt-danger-text)',
          !local && 'text-aera-lightslate italic font-normal hover:text-aera-gray',
          !multiline && 'block',
          multiline && 'min-h-[72px] align-top'
        )}
      />
      {autoSave && status === 'saving' && (
        <div className={cn('absolute right-0 flex items-center justify-center', multiline ? 'top-0 pt-1' : 'top-1/2 -translate-y-1/2')}>
          <Loader2 className="w-3.5 h-3.5 animate-spin text-aera-lightslate" />
        </div>
      )}
    </div>
  )
}
